'use strict'

class Helper {
  static isDefined(value) {
    return typeof value !== 'undefined' && value !== null
  }

  static isNull(value) {
    return !Helper.isDefined(value)
  }

  static isNotEmpty(value) {
    return !Helper.isEmpty(value)
  }

  static isNotEmptyMap(map) {
    return !Helper.isEmptyMap(map)
  }

  static isEmptyMap(map) {
    return this.isNull(map) || map.size === 0
  }

  static isEmpty(obj) {
    // null and undefined are "empty"
    if (obj == null) return true

    // Assume if it has a length property with a non-zero value
    // that that property is correct.
    if (obj.length > 0) return false
    if (obj.length === 0) return true

    // If it isn't an object at this point
    // it is empty, but it can't be anything *but* empty
    // Is it empty?  Depends on your application.
    if (typeof obj !== 'object') return true

    // Otherwise, does it have any properties of its own?
    // Note that this doesn't handle
    // toString and valueOf enumeration bugs in IE < 9
    for (const key in obj) {
      /* eslint no-prototype-builtins: "error" */
      if (Object.prototype.hasOwnProperty.call(obj, key)) return false
    }

    return true
  }

  static isEquals(a, b) {
    // Create arrays of property names
    const aProps = Object.getOwnPropertyNames(a)
    const bProps = Object.getOwnPropertyNames(b)

    // If number of properties is different,
    // objects are not equivalent
    if (aProps.length !== bProps.length) {
      return false
    }

    for (let i = 0; i < aProps.length; i++) {
      const propName = aProps[i]

      // If values of same property are not equal,
      // objects are not equivalent
      if (a[propName] !== b[propName]) {
        return false
      }
    }

    // If we made it this far, objects
    // are considered equivalent
    return true
  }

  static getValue(value, defValue) {
    return Helper.isDefined(value) ? value : (Helper.isDefined(defValue) ? defValue : null)
  }

  static getNotEmptyValue(value, defValue) {
    const valueString = Helper.isDefined(value) ? value.toString() : null
    const defValueString = Helper.isDefined(defValue) ? defValue.toString() : null
    return Helper.isNotEmpty(valueString) ? value : (Helper.isNotEmpty(defValueString) ? defValue : null)
  }

  static cloneObj(obj) {
    const result = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = obj[key]
      }
    }
    return result
  }

  static isInteger(num) {
    return (num ^ 0) === num
  }

  static getCaseInsensitiveAttr(obj, key, defValue = null) {
    const caseInsensitiveObj = Object.keys(obj).reduce((c, k) => {
      c[k.toLowerCase()] = obj[k]
      return c
    }, {})

    return this.getValue(caseInsensitiveObj[key.toLowerCase()], defValue)
  }

  static getBoolean(value) {
    switch (value) {
      case true:
      case 'true':
        return true
      default:
        return false
    }
  }

  static checkString(fieldName, field) {
    if (!Helper.isDefined(field)) {
      throw new Error(`${fieldName} is undefined`)
    }
    if (typeof (field) !== 'string') {
      throw new Error(`${fieldName} is not string type: ${field}`)
    }
    if (Helper.isEmpty(field)) {
      throw new Error(`${fieldName} is empty`)
    }
  }

  static checkOptionalString(fieldName, field) {
    if (!Helper.isDefined(field)) {
      return
    }
    this.checkString(fieldName, field)
  }

  static checkLong(fieldName, field) {
    if (Helper.isNull(field) || !Number.isInteger(field)) {
      throw new Error(`Incorrect long value ${fieldName}: '${field}'`)
    }
  }

  static checkNonNegativeLong(fieldName, field) {
    this.checkLong(fieldName, field)
    if (field < 0) {
      throw new Error(`${fieldName} should be not less then 0: '${field}'`)
    }
  }

  static checkArray(fieldName, field) {
    if (!Array.isArray(field)) {
      throw new Error(`${fieldName} is not array: '${field}'`)
    }
  }

  static checkNotEmptyArray(fieldName, field) {
    this.checkArray(fieldName, field)
    if (!field.length) {
      throw new Error(`${fieldName} is empty`)
    }
  }

  static checkIsNotEmpty(fieldName, field) {
    if (Helper.isEmpty(field)) {
      throw new Error(`${fieldName} is empty`)
    }
  }

  static checkSomeValueRequired (fieldName, value, someFieldName, someValue) {
    if (this.isNull(value) && this.isNull(someValue)) {
      throw new Error(`${fieldName} or ${someFieldName} should be defined`)
    }
  }
}

export default Helper
